import React from 'react';
import PropTypes from 'prop-types';

/*
 * Icon collection: several related icons are intentionally colocated in one
 * file (an icon set), so multiple components per file is expected here.
 */
/* eslint-disable react/no-multi-comp */

/**
 * AriesIcons — single source of the Aries outline icons (Lucide glyph set),
 * rendered as stroke-based inline SVG so they match the Figma exactly.
 */
const baseProps = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className,
  'aria-hidden': true,
  focusable: false,
});

/*
 * Base props for the 22x22 Figma icon set (stroke width 1.375). Kept separate
 * from the 24x24 Lucide set so each set renders at its native proportions.
 */
const baseProps22 = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 22 22',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.375,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className,
  'aria-hidden': true,
  focusable: false,
});

export const UsersIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M14.6668 19.25V17.4167C14.6668 16.4442 14.2805 15.5116 13.5929 14.8239C12.9053 14.1363 11.9726 13.75 11.0002 13.75H5.50016C4.5277 13.75 3.59507 14.1363 2.90744 14.8239C2.2198 15.5116 1.8335 16.4442 1.8335 17.4167V19.25M20.1668 19.2499V17.4166C20.1662 16.6041 19.8958 15.8149 19.3981 15.1728C18.9003 14.5308 18.2034 14.0722 17.4168 13.8691M14.6668 2.86906C15.4555 3.071 16.1546 3.5297 16.6538 4.17284C17.153 4.81599 17.424 5.60699 17.424 6.42114C17.424 7.2353 17.153 8.0263 16.6538 8.66944C16.1546 9.31258 15.4555 9.77128 14.6668 9.97323M11.9168 6.41667C11.9168 8.44171 10.2752 10.0833 8.25016 10.0833C6.22512 10.0833 4.5835 8.44171 4.5835 6.41667C4.5835 4.39162 6.22512 2.75 8.25016 2.75C10.2752 2.75 11.9168 4.39162 11.9168 6.41667Z" />
  </svg>
);

export const SchedulingIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M4.58333 3.66666H17.4167C18.4292 3.66666 19.25 4.48747 19.25 5.49999V18.3333C19.25 19.3458 18.4292 20.1667 17.4167 20.1667H4.58333C3.57081 20.1667 2.75 19.3458 2.75 18.3333V5.49999C2.75 4.48747 3.57081 3.66666 4.58333 3.66666Z" />
    <path d="M14.6665 1.83334V5.50001" strokeWidth="1.14583" />
    <path d="M7.3335 1.83334V5.50001" strokeWidth="1.14583" />
    <path d="M2.75 9.16666H19.25" strokeWidth="1.14583" />
  </svg>
);

export const DashboardIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M11.0002 12.8333L14.6668 9.16666M3.06183 17.4166C2.2572 16.0231 1.83356 14.4424 1.8335 12.8332C1.83343 11.2241 2.25695 9.64331 3.06148 8.24975C3.86601 6.85618 5.02319 5.69895 6.41673 4.89437C7.81026 4.08979 9.39104 3.66621 11.0002 3.66621C12.6093 3.66621 14.1901 4.08979 15.5836 4.89437C16.9771 5.69895 18.1343 6.85618 18.9388 8.24975C19.7434 9.64331 20.1669 11.2241 20.1668 12.8332C20.1668 14.4424 19.7431 16.0231 18.9385 17.4166" />
  </svg>
);

export const TeamIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M17.4168 19.25V17.4167C17.4168 16.4442 17.0305 15.5116 16.3429 14.8239C15.6553 14.1363 14.7226 13.75 13.7502 13.75H8.25016C7.2777 13.75 6.34507 14.1363 5.65744 14.8239C4.9698 15.5116 4.5835 16.4442 4.5835 17.4167V19.25M14.6668 6.41667C14.6668 8.44171 13.0252 10.0833 11.0002 10.0833C8.97512 10.0833 7.3335 8.44171 7.3335 6.41667C7.3335 4.39162 8.97512 2.75 11.0002 2.75C13.0252 2.75 14.6668 4.39162 14.6668 6.41667Z" />
  </svg>
);

export const JobsIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M14.6665 3.66668H16.4998C16.9861 3.66668 17.4524 3.85983 17.7962 4.20365C18.14 4.54746 18.3332 5.01378 18.3332 5.50001V18.3333C18.3332 18.8196 18.14 19.2859 17.7962 19.6297C17.4524 19.9735 16.9861 20.1667 16.4998 20.1667H5.49984C5.01361 20.1667 4.54729 19.9735 4.20347 19.6297C3.85966 19.2859 3.6665 18.8196 3.6665 18.3333V5.50001C3.6665 5.01378 3.85966 4.54746 4.20347 4.20365C4.54729 3.85983 5.01361 3.66668 5.49984 3.66668H7.33317M8.24984 12.8333L10.0832 14.6667L13.7498 11M8.24984 1.83334H13.7498C14.2561 1.83334 14.6665 2.24375 14.6665 2.75001V4.58334C14.6665 5.0896 14.2561 5.50001 13.7498 5.50001H8.24984C7.74358 5.50001 7.33317 5.0896 7.33317 4.58334V2.75001C7.33317 2.24375 7.74358 1.83334 8.24984 1.83334Z" />
  </svg>
);

export const ReportsIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M7.33317 3.66668H5.49984C5.01361 3.66668 4.54729 3.85983 4.20347 4.20365C3.85966 4.54746 3.6665 5.01378 3.6665 5.50001V18.3333C3.6665 18.8196 3.85966 19.2859 4.20347 19.6297C4.54729 19.9735 5.01361 20.1667 5.49984 20.1667H16.4998C16.9861 20.1667 17.4524 19.9735 17.7962 19.6297C18.14 19.2859 18.3332 18.8196 18.3332 18.3333V17.875M14.6665 3.66668H16.4998C16.8213 3.66701 17.1371 3.75187 17.4154 3.91277C17.6938 4.07366 17.9249 4.30492 18.0857 4.58334M7.33317 16.5H8.24984M8.24984 1.83334H13.7498C14.2561 1.83334 14.6665 2.24375 14.6665 2.75001V4.58334C14.6665 5.0896 14.2561 5.50001 13.7498 5.50001H8.24984C7.74358 5.50001 7.33317 5.0896 7.33317 4.58334V2.75001C7.33317 2.24375 7.74358 1.83334 8.24984 1.83334ZM19.5963 11.5738C19.9615 11.2086 20.1666 10.7133 20.1666 10.1969C20.1666 9.68051 19.9615 9.18525 19.5963 8.82009C19.2312 8.45493 18.7359 8.24979 18.2195 8.24979C17.7031 8.24979 17.2078 8.45493 16.8427 8.82009L13.1668 12.4978C12.9489 12.7156 12.7894 12.9848 12.703 13.2806L11.9357 15.9114C11.9127 15.9903 11.9113 16.0739 11.9317 16.1535C11.9521 16.2331 11.9935 16.3057 12.0516 16.3638C12.1097 16.4219 12.1824 16.4634 12.262 16.4838C12.3416 16.5041 12.4252 16.5028 12.5041 16.4798L15.1349 15.7125C15.4307 15.6261 15.6999 15.4666 15.9177 15.2487L19.5963 11.5738Z" />
  </svg>
);

export const DeficienciesIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M11.0002 7.33334V11M11.0002 14.6667H11.0093M7.20516 1.83334H14.7952L20.1668 7.20501V14.795L14.7952 20.1667H7.20516L1.8335 14.795V7.20501L7.20516 1.83334Z" />
  </svg>
);

export const ServiceRequestsIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M13.4752 5.77521C13.3073 5.94656 13.2132 6.17693 13.2132 6.41687C13.2132 6.65681 13.3073 6.88719 13.4752 7.05854L14.9419 8.52521C15.1133 8.69316 15.3436 8.78724 15.5836 8.78724C15.8235 8.78724 16.0539 8.69316 16.2252 8.52521L18.7441 6.00638C19.1971 5.55335 19.9602 5.70065 20.0774 6.33051C20.1992 6.98525 20.2018 7.66034 20.0812 8.32276C19.8807 9.42267 19.3499 10.4354 18.5593 11.226C17.7688 12.0165 16.756 12.5474 15.6561 12.7478C14.765 12.9102 13.8508 12.8494 12.9942 12.5753C12.6308 12.459 12.2241 12.5263 11.9544 12.7961L6.06857 18.6819C5.70389 19.0465 5.20929 19.2514 4.69357 19.2514C4.17784 19.2514 3.68324 19.0465 3.31857 18.6819C2.95389 18.3172 2.74902 17.8226 2.74902 17.3069C2.74902 16.7911 2.95389 16.2965 3.31857 15.9319L9.20435 10.0461C9.47414 9.7763 9.54141 9.3696 9.42514 9.00621C9.15107 8.14959 9.09028 7.23548 9.25265 6.34432C9.45306 5.2444 9.98391 4.23168 10.7745 3.44112C11.565 2.65055 12.5778 2.11969 13.6777 1.91929C14.3401 1.79859 15.0152 1.8012 15.6699 1.92306C16.2998 2.04028 16.4471 2.80335 15.9941 3.25638L13.4844 5.76604L13.4752 5.77521Z" />
  </svg>
);

export const InventoryIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M10.9994 20.1669V11.9169M18.3327 11.9169V15.4644C18.3331 15.8104 18.2384 16.1498 18.0589 16.4457C17.8795 16.7415 17.6222 16.9823 17.3152 17.1419L11.8152 19.9652C11.5632 20.0962 11.2834 20.1646 10.9994 20.1646C10.7154 20.1646 10.4356 20.0962 10.1836 19.9652L4.68356 17.1419C4.37655 16.9823 4.11928 16.7415 3.93985 16.4457C3.76041 16.1498 3.6657 15.8104 3.66606 15.4644V11.9169M13.9052 2.02599C14.1335 1.89834 14.3908 1.83132 14.6523 1.83132C14.9139 1.83132 15.1711 1.89834 15.3994 2.02599L19.2494 4.18933C19.5221 4.34351 19.7489 4.56733 19.9067 4.8379C20.0646 5.10847 20.1477 5.41609 20.1477 5.72933C20.1477 6.04257 20.0646 6.35019 19.9067 6.62076C19.7489 6.89133 19.5221 7.11514 19.2494 7.26933L8.08441 13.5577C7.85542 13.6883 7.59636 13.757 7.33274 13.757C7.06913 13.757 6.81006 13.6883 6.58107 13.5577L2.74941 11.3943C2.47674 11.2401 2.2499 11.0163 2.09207 10.7458C1.93424 10.4752 1.85107 10.1676 1.85107 9.85433C1.85107 9.54109 1.93424 9.23347 2.09207 8.9629C2.2499 8.69233 2.47674 8.46851 2.74941 8.31433L13.9052 2.02599ZM19.2494 11.3944C19.5221 11.2402 19.7489 11.0164 19.9067 10.7458C20.0646 10.4753 20.1477 10.1676 20.1477 9.85439C20.1477 9.54115 20.0646 9.23353 19.9067 8.96296C19.7489 8.69239 19.5221 8.46857 19.2494 8.31439L8.09357 2.01689C7.86613 1.88664 7.60859 1.81812 7.34649 1.81812C7.08439 1.81812 6.82685 1.88664 6.59941 2.01689L2.74941 4.18939C2.47674 4.34357 2.2499 4.56739 2.09207 4.83796C1.93424 5.10853 1.85107 5.41615 1.85107 5.72939C1.85107 6.04263 1.93424 6.35025 2.09207 6.62082C2.2499 6.89139 2.47674 7.1152 2.74941 7.26939L13.9144 13.5577C14.1417 13.6883 14.3993 13.7571 14.6615 13.7571C14.9237 13.7571 15.1813 13.6883 15.4086 13.5577L19.2494 11.3944Z" />
  </svg>
);

export const BugTicketsIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M7.33301 1.83325L9.05634 3.55659M12.9431 3.55659L14.6664 1.83325M8.24974 6.53586V5.61919C8.23312 5.24771 8.29194 4.87671 8.42265 4.52859C8.55335 4.18046 8.75324 3.86243 9.01023 3.59368C9.26723 3.32493 9.57602 3.11103 9.91795 2.9649C10.2599 2.81877 10.6279 2.74343 10.9997 2.74343C11.3716 2.74343 11.7396 2.81877 12.0815 2.9649C12.4235 3.11103 12.7322 3.32493 12.9892 3.59368C13.2462 3.86243 13.4461 4.18046 13.5768 4.52859C13.7075 4.87671 13.7664 5.24771 13.7497 5.61919V6.53586M10.9997 18.3333C7.97467 18.3333 5.49967 15.8583 5.49967 12.8333V10.0833C5.49967 9.11079 5.88598 8.17816 6.57362 7.49053C7.26125 6.80289 8.19388 6.41659 9.16634 6.41659H12.833C13.8055 6.41659 14.7381 6.80289 15.4257 7.49053C16.1134 8.17816 16.4997 9.11079 16.4997 10.0833V12.8333C16.4997 15.8583 14.0247 18.3333 10.9997 18.3333ZM10.9997 18.3333V10.0833M5.98551 8.24992C4.21634 8.06659 2.74967 6.50825 2.74967 4.58325M5.49967 11.9166H1.83301M2.74967 19.2499C2.74967 17.3249 4.30801 15.6749 6.23301 15.5833M19.2224 4.58325C19.2224 6.50825 17.7557 8.06659 16.014 8.24992M20.1663 11.9166H16.4997M15.7665 15.5833C17.6915 15.6749 19.2499 17.3249 19.2499 19.2499" />
  </svg>
);

export const SettingsIcon = ({ size, className }) => (
  <svg {...baseProps22(size, className)}>
    <path d="M11.2022 1.83325H10.7988C10.3126 1.83325 9.84628 2.02641 9.50246 2.37022C9.15864 2.71404 8.96549 3.18036 8.96549 3.66659V3.83159C8.96516 4.15308 8.88029 4.46884 8.7194 4.74719C8.55851 5.02553 8.32725 5.25667 8.04882 5.41742L7.65466 5.64659C7.37596 5.80749 7.05981 5.89221 6.73799 5.89221C6.41617 5.89221 6.10003 5.80749 5.82132 5.64659L5.68382 5.57325C5.26313 5.33058 4.76334 5.26474 4.29416 5.3902C3.82498 5.51566 3.42475 5.82216 3.18132 6.24242L2.97966 6.59075C2.73698 7.01144 2.67115 7.51124 2.79661 7.98042C2.92206 8.4496 3.22856 8.84983 3.64882 9.09325L3.78632 9.18492C4.06341 9.34489 4.29381 9.57458 4.45462 9.85118C4.61543 10.1278 4.70106 10.4416 4.70299 10.7616V11.2291C4.70427 11.5521 4.62017 11.8698 4.4592 12.1499C4.29822 12.43 4.0661 12.6626 3.78632 12.8241L3.64882 12.9066C3.22856 13.15 2.92206 13.5502 2.79661 14.0194C2.67115 14.4886 2.73698 14.9884 2.97966 15.4091L3.18132 15.7574C3.42475 16.1777 3.82498 16.4842 4.29416 16.6096C4.76334 16.7351 5.26313 16.6693 5.68382 16.4266L5.82132 16.3533C6.10003 16.1923 6.41617 16.1076 6.73799 16.1076C7.05981 16.1076 7.37596 16.1923 7.65466 16.3533L8.04882 16.5824C8.32725 16.7432 8.55851 16.9743 8.7194 17.2527C8.88029 17.531 8.96516 17.8468 8.96549 18.1683V18.3333C8.96549 18.8195 9.15864 19.2858 9.50246 19.6296C9.84628 19.9734 10.3126 20.1666 10.7988 20.1666H11.2022C11.6884 20.1666 12.1547 19.9734 12.4985 19.6296C12.8423 19.2858 13.0355 18.8195 13.0355 18.3333V18.1683C13.0358 17.8468 13.1207 17.531 13.2816 17.2527C13.4425 16.9743 13.6737 16.7432 13.9522 16.5824L14.3463 16.3533C14.625 16.1923 14.9412 16.1076 15.263 16.1076C15.5848 16.1076 15.901 16.1923 16.1797 16.3533L16.3172 16.4266C16.7378 16.6693 17.2376 16.7351 17.7068 16.6096C18.176 16.4842 18.5762 16.1777 18.8197 15.7574L19.0213 15.3999C19.264 14.9792 19.3298 14.4794 19.2044 14.0103C19.0789 13.5411 18.7724 13.1408 18.3522 12.8974L18.2147 12.8241C17.9349 12.6626 17.7028 12.43 17.5418 12.1499C17.3808 11.8698 17.2967 11.5521 17.298 11.2291V10.7708C17.2967 10.4477 17.3808 10.13 17.5418 9.84995C17.7028 9.56986 17.9349 9.33728 18.2147 9.17575L18.3522 9.09325C18.7724 8.84983 19.0789 8.4496 19.2044 7.98042C19.3298 7.51124 19.264 7.01144 19.0213 6.59075L18.8197 6.24242C18.5762 5.82216 18.176 5.51566 17.7068 5.3902C17.2376 5.26474 16.7378 5.33058 16.3172 5.57325L16.1797 5.64659C15.901 5.80749 15.5848 5.89221 15.263 5.89221C14.9412 5.89221 14.625 5.80749 14.3463 5.64659L13.9522 5.41742C13.6737 5.25667 13.4425 5.02553 13.2816 4.74719C13.1207 4.46884 13.0358 4.15308 13.0355 3.83159V3.66659C13.0355 3.18036 12.8423 2.71404 12.4985 2.37022C12.1547 2.02641 11.6884 1.83325 11.2022 1.83325ZM13.7505 10.9999C13.7505 12.5187 12.5193 13.7499 11.0005 13.7499C9.48171 13.7499 8.25049 12.5187 8.25049 10.9999C8.25049 9.48114 9.48171 8.24992 11.0005 8.24992C12.5193 8.24992 13.7505 9.48114 13.7505 10.9999Z" />
  </svg>
);

export const GoToProfileIcon = ({ size, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden
    focusable={false}
  >
    <path d="M15.6253 5V13.125C15.6253 13.2908 15.5595 13.4497 15.4423 13.5669C15.3251 13.6842 15.1661 13.75 15.0003 13.75C14.8346 13.75 14.6756 13.6842 14.5584 13.5669C14.4412 13.4497 14.3753 13.2908 14.3753 13.125V6.50859L5.44254 15.4422C5.32526 15.5595 5.1662 15.6253 5.00035 15.6253C4.8345 15.6253 4.67544 15.5595 4.55816 15.4422C4.44088 15.3249 4.375 15.1659 4.375 15C4.375 14.8341 4.44088 14.6751 4.55816 14.5578L13.4918 5.625H6.87535C6.70959 5.625 6.55062 5.55915 6.43341 5.44194C6.3162 5.32473 6.25035 5.16576 6.25035 5C6.25035 4.83424 6.3162 4.67527 6.43341 4.55806C6.55062 4.44085 6.70959 4.375 6.87535 4.375H15.0003C15.1661 4.375 15.3251 4.44085 15.4423 4.55806C15.5595 4.67527 15.6253 4.83424 15.6253 5Z" />
  </svg>
);

export const ItemsNeededIcon = ({ size, className }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
    focusable={false}
  >
    <path d="M19.0443 14.4034C18.2212 13.5797 17.0791 13.2307 15.6123 13.6238L10.382 8.3883L10.4512 8.12691C10.7524 6.99667 10.4269 5.78177 9.602 4.95685C8.76207 4.11628 7.56799 3.80786 6.4387 4.11806C6.35957 4.13978 6.28752 4.1819 6.22978 4.24021C6.17203 4.29851 6.13061 4.37096 6.10965 4.4503C6.08876 4.52962 6.08904 4.61304 6.11048 4.69221C6.13192 4.77139 6.17375 4.84356 6.23181 4.90151L7.49117 6.16085C7.85935 6.52993 7.85576 7.12102 7.49208 7.48566C7.13128 7.84743 6.5248 7.84687 6.16543 7.48657L4.90582 6.22688C4.84778 6.16882 4.7755 6.12702 4.69622 6.10568C4.61695 6.08434 4.53345 6.0842 4.4541 6.10528C4.37479 6.12638 4.3024 6.16795 4.2442 6.22582C4.186 6.28368 4.14402 6.35583 4.12246 6.43502C3.81678 7.55189 4.11749 8.75335 4.96145 9.5973C5.78419 10.4206 6.92486 10.7708 8.39248 10.3769L13.6232 15.6124C13.2328 17.0787 13.5695 18.2097 14.4037 19.0439C15.2447 19.8855 16.4393 20.1916 17.5661 19.8824C17.6453 19.8607 17.7173 19.8186 17.7751 19.7603C17.8329 19.702 17.8743 19.6295 17.8953 19.5502C17.9162 19.4709 17.9159 19.3874 17.8945 19.3082C17.873 19.229 17.8312 19.1569 17.7731 19.0989L16.514 17.8399C16.1459 17.4708 16.1494 16.8796 16.5131 16.515C16.8784 16.1516 17.4836 16.1574 17.8398 16.5146L19.0995 17.7739C19.1575 17.832 19.2298 17.8737 19.3091 17.8951C19.3883 17.9164 19.4718 17.9165 19.5512 17.8955C19.6305 17.8743 19.7028 17.8328 19.761 17.7749C19.8192 17.7171 19.8612 17.645 19.8828 17.5658C20.1885 16.4491 19.8878 15.2473 19.0443 14.4034Z" />
    <path d="M9.01728 12.3312L4.5485 16.8004C3.81717 17.5318 3.81717 18.721 4.5485 19.4523C5.27982 20.1836 6.46909 20.1836 7.20039 19.4523L11.6691 14.9831L9.01728 12.3312ZM6.75998 17.9975C6.57684 18.1806 6.28026 18.1806 6.09713 17.9975C5.91402 17.8144 5.91402 17.5178 6.09713 17.3347L8.94043 14.4909C9.12354 14.3078 9.42021 14.3078 9.60329 14.4909C9.78639 14.674 9.78639 14.9706 9.60329 15.1537L6.75998 17.9975ZM19.8631 4.94545L19.055 4.13732C18.9811 4.0634 18.8846 4.01631 18.7808 4.00352C18.6771 3.99073 18.572 4.01299 18.4824 4.06675L15.9964 5.55817C15.7348 5.71509 15.6904 6.07595 15.906 6.29161L16.1039 6.48944L12.9481 9.63231L14.2743 10.9585L17.4301 7.81559L17.7088 8.09439C17.9245 8.30999 18.2854 8.26562 18.4423 8.00404L19.9337 5.51808C19.9875 5.42842 20.0097 5.32337 19.9969 5.21962C19.9841 5.11587 19.937 5.01937 19.8631 4.94545Z" />
  </svg>
);

/*
 * Base props for the 20x20 top-nav icon set (stroke width 1.25).
 */
const baseProps20 = (size, className) => ({
  width: size,
  height: size,
  viewBox: '0 0 20 20',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.25,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  className,
  'aria-hidden': true,
  focusable: false,
});

export const ChatIcon = ({ size, className }) => (
  <svg {...baseProps20(size, className)}>
    <path d="M6.66667 8.33333H6.675M10 8.33333H10.0083M13.3333 8.33333H13.3417M17.5 12.5C17.5 12.942 17.3244 13.366 17.0118 13.6785C16.6993 13.9911 16.2754 14.1667 15.8333 14.1667H5.83333L2.5 17.5V4.16667C2.5 3.72464 2.67559 3.30072 2.98816 2.98816C3.30072 2.67559 3.72464 2.5 4.16667 2.5H15.8333C16.2754 2.5 16.6993 2.67559 17.0118 2.98816C17.3244 3.30072 17.5 3.72464 17.5 4.16667V12.5Z" />
  </svg>
);

export const OfflineIcon = ({ size, className }) => (
  <svg {...baseProps20(size, className)}>
    <path d="M1.6665 1.66667L18.3332 18.3333M4.8182 4.81832C3.65676 5.41885 2.73075 6.39217 2.18878 7.58207C1.64682 8.77197 1.52033 10.1094 1.82962 11.3798C2.1389 12.6503 2.86603 13.7799 3.8943 14.5875C4.92257 15.3951 6.19236 15.8339 7.49987 15.8333H14.5832C14.9522 15.8336 15.3192 15.7794 15.6724 15.6725M17.9432 13.7501C18.2269 13.1783 18.3599 12.5435 18.3297 11.906C18.2995 11.2684 18.1071 10.6491 17.7706 10.1067C17.4341 9.56425 16.9648 9.1167 16.407 8.80639C15.8492 8.49608 15.2215 8.33327 14.5832 8.3334H13.0915C12.7727 7.26612 12.1554 6.31227 11.3123 5.58434C10.4692 4.85642 9.43552 4.38478 8.33317 4.22506" />
  </svg>
);

export const NotificationIcon = ({ size, className }) => (
  <svg {...baseProps20(size, className)}>
    <path d="M8.58337 17.5C8.72286 17.7537 8.92791 17.9653 9.17712 18.1127C9.42632 18.26 9.71052 18.3378 10 18.3378C10.2896 18.3378 10.5738 18.26 10.823 18.1127C11.0722 17.9653 11.2772 17.7537 11.4167 17.5M5 6.66667C5 5.34059 5.52678 4.06882 6.46447 3.13114C7.40215 2.19346 8.67392 1.66667 10 1.66667C11.3261 1.66667 12.5979 2.19346 13.5355 3.13114C14.4732 4.06882 15 5.34059 15 6.66667C15 12.5 17.5 14.1667 17.5 14.1667H2.5C2.5 14.1667 5 12.5 5 6.66667Z" />
  </svg>
);

export const AlarmClockIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2 2" />
    <path d="M5 3 2 6" />
    <path d="m22 6-3-3" />
    <path d="M6.38 18.7 4 21" />
    <path d="M17.64 18.67 20 21" />
  </svg>
);

export const OctagonAlertIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M12 16h.01" />
    <path d="M12 8v4" />
    <path d="M15.312 2a2 2 0 0 1 1.414.586l4.688 4.688A2 2 0 0 1 22 8.688v6.624a2 2 0 0 1-.586 1.414l-4.688 4.688a2 2 0 0 1-1.414.586H8.688a2 2 0 0 1-1.414-.586l-4.688-4.688A2 2 0 0 1 2 15.312V8.688a2 2 0 0 1 .586-1.414l4.688-4.688A2 2 0 0 1 8.688 2z" />
  </svg>
);

export const HandshakeIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m11 17 2 2a1 1 0 1 0 3-3" />
    <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
    <path d="m21 3 1 11h-2" />
    <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
    <path d="M3 4h8" />
  </svg>
);

export const ContactIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M16 2v2" />
    <path d="M7 21v-2a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2" />
    <path d="M8 2v2" />
    <circle cx="12" cy="10" r="3" />
    <rect x="3" y="3" width="18" height="18" rx="2" />
  </svg>
);

export const ArrowUpRightIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M7 7h10v10" />
    <path d="M7 17 17 7" />
  </svg>
);

export const UploadIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" />
    <path d="M7 8L12 3L17 8" />
    <path d="M12 3V15" />
  </svg>
);

export const FileIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
  </svg>
);

export const FileImageIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <circle cx="10" cy="12" r="2" />
    <path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22" />
  </svg>
);

export const FileVideoIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="m10 11 5 3-5 3v-6Z" />
  </svg>
);

export const FileAudioIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M8 18v-5l4-1v5" />
    <circle cx="7" cy="18" r="1.5" />
    <circle cx="11" cy="17" r="1.5" />
  </svg>
);

export const FileTextIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

export const FileSpreadsheetIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M8 13h2" />
    <path d="M14 13h2" />
    <path d="M8 17h2" />
    <path d="M14 17h2" />
  </svg>
);

export const HandCoinsIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M11 15h2a2 2 0 1 0 0-4h-3c-.6 0-1.1.2-1.4.6L3 17" />
    <path d="m7 21 1.6-1.4c.3-.4.8-.6 1.4-.6h4c1.1 0 2.1-.4 2.8-1.2l4.6-4.4a2 2 0 0 0-2.75-2.91l-4.2 3.9" />
    <path d="m2 16 6 6" />
    <circle cx="16" cy="9" r="2.9" />
    <circle cx="6" cy="5" r="3" />
  </svg>
);

export const FunnelIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M10 20a1 1 0 0 0 .553.895l2 1A1 1 0 0 0 14 21v-7a2 2 0 0 1 .517-1.341L21.74 4.67A1 1 0 0 0 21 3H3a1 1 0 0 0-.742 1.67l7.225 7.989A2 2 0 0 1 10 14z" />
  </svg>
);

export const ListChecksIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M13 5h8" />
    <path d="M13 12h8" />
    <path d="M13 19h8" />
    <path d="m3 17 2 2 4-4" />
    <path d="m3 7 2 2 4-4" />
  </svg>
);

export const CalendarIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M8 2v3" />
    <path d="M16 2v3" />
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <path d="M3 9h18" />
  </svg>
);

export const ClipboardListIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <path d="M12 11h4" />
    <path d="M12 16h4" />
    <path d="M8 11h.01" />
    <path d="M8 16h.01" />
  </svg>
);

export const ChevronDownIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const ChevronRightIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);

export const ChevronLeftIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m15 18-6-6 6-6" />
  </svg>
);

export const ChevronUpIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m6 15 6-6 6 6" />
  </svg>
);

export const ArrowLeftIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m12 19-7-7 7-7" />
    <path d="M19 12H5" />
  </svg>
);

export const XIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export const SquarePenIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z" />
  </svg>
);

export const LockKeyholeIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="12" cy="16" r="1" />
    <rect x="3" y="10" width="18" height="12" rx="2" />
    <path d="M7 10V7a5 5 0 0 1 10 0v3" />
  </svg>
);

export const LogOutIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
  </svg>
);

export const PlusIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M5 12h14" />
    <path d="M12 5v14" />
  </svg>
);

export const CheckIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

export const TrashIcon = ({ size, className }) => (
  <svg {...baseProps20(size, className)}>
    <path d="M2.5 4.99935H17.5M15.8333 4.99935V16.666C15.8333 17.4993 15 18.3327 14.1667 18.3327H5.83333C5 18.3327 4.16667 17.4993 4.16667 16.666V4.99935M6.66667 4.99935V3.33268C6.66667 2.49935 7.5 1.66602 8.33333 1.66602H11.6667C12.5 1.66602 13.3333 2.49935 13.3333 3.33268V4.99935" />
  </svg>
);

export const ClockIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l4 2" />
  </svg>
);

export const InfoIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

export const MapIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M14.106 5.553a2 2 0 0 0 1.788 0l3.659-1.83A1 1 0 0 1 21 4.619v12.764a1 1 0 0 1-.553.894l-4.553 2.277a2 2 0 0 1-1.788 0l-4.212-2.106a2 2 0 0 0-1.788 0l-3.659 1.83A1 1 0 0 1 3 19.381V6.618a1 1 0 0 1 .553-.894l4.553-2.277a2 2 0 0 1 1.788 0z" />
    <path d="M15 5.764v15" />
    <path d="M9 3.236v15" />
  </svg>
);

export const SearchIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
  </svg>
);

export const HistoryIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
    <path d="M3 3v5h5" />
    <path d="M12 7v5l4 2" />
  </svg>
);

export const EllipsisVerticalIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <circle cx="12" cy="12" r="1" />
    <circle cx="12" cy="5" r="1" />
    <circle cx="12" cy="19" r="1" />
  </svg>
);

export const Maximize2Icon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M15 3h6v6" />
    <path d="M9 21H3v-6" />
    <path d="M21 3l-7 7" />
    <path d="M3 21l7-7" />
  </svg>
);

export const Minimize2Icon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M4 14h6v6" />
    <path d="M20 10h-6V4" />
    <path d="M14 10l7-7" />
    <path d="M3 21l7-7" />
  </svg>
);

export const SendIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
    <path d="m21.854 2.147-10.94 10.939" />
  </svg>
);

export const PaperclipIcon = ({ size, className }) => (
  <svg {...baseProps(size, className)}>
    <path d="M13.234 20.252 21 12.3" />
    <path d="m16 6-8.414 8.586a2 2 0 0 0 0 2.828 2 2 0 0 0 2.828 0l8.414-8.586a4 4 0 0 0 0-5.656 4 4 0 0 0-5.656 0l-8.415 8.585a6 6 0 1 0 8.486 8.486" />
  </svg>
);

const iconPropTypes = {
  size: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  className: PropTypes.string,
};
const iconDefaultProps = { size: 24, className: '' };

AlarmClockIcon.propTypes = iconPropTypes;
AlarmClockIcon.defaultProps = iconDefaultProps;
OctagonAlertIcon.propTypes = iconPropTypes;
OctagonAlertIcon.defaultProps = iconDefaultProps;
HandshakeIcon.propTypes = iconPropTypes;
HandshakeIcon.defaultProps = iconDefaultProps;
ContactIcon.propTypes = iconPropTypes;
ContactIcon.defaultProps = iconDefaultProps;
ArrowUpRightIcon.propTypes = iconPropTypes;
ArrowUpRightIcon.defaultProps = iconDefaultProps;
UploadIcon.propTypes = iconPropTypes;
UploadIcon.defaultProps = iconDefaultProps;
FileIcon.propTypes = iconPropTypes;
FileIcon.defaultProps = iconDefaultProps;
FileImageIcon.propTypes = iconPropTypes;
FileImageIcon.defaultProps = iconDefaultProps;
FileVideoIcon.propTypes = iconPropTypes;
FileVideoIcon.defaultProps = iconDefaultProps;
FileAudioIcon.propTypes = iconPropTypes;
FileAudioIcon.defaultProps = iconDefaultProps;
FileTextIcon.propTypes = iconPropTypes;
FileTextIcon.defaultProps = iconDefaultProps;
FileSpreadsheetIcon.propTypes = iconPropTypes;
FileSpreadsheetIcon.defaultProps = iconDefaultProps;
HandCoinsIcon.propTypes = iconPropTypes;
HandCoinsIcon.defaultProps = iconDefaultProps;
FunnelIcon.propTypes = iconPropTypes;
FunnelIcon.defaultProps = iconDefaultProps;
ListChecksIcon.propTypes = iconPropTypes;
ListChecksIcon.defaultProps = iconDefaultProps;
CalendarIcon.propTypes = iconPropTypes;
CalendarIcon.defaultProps = iconDefaultProps;
ClipboardListIcon.propTypes = iconPropTypes;
ClipboardListIcon.defaultProps = iconDefaultProps;
ChevronDownIcon.propTypes = iconPropTypes;
ChevronDownIcon.defaultProps = iconDefaultProps;
ChevronRightIcon.propTypes = iconPropTypes;
ChevronRightIcon.defaultProps = iconDefaultProps;
ChevronLeftIcon.propTypes = iconPropTypes;
ChevronLeftIcon.defaultProps = iconDefaultProps;
ChevronUpIcon.propTypes = iconPropTypes;
ChevronUpIcon.defaultProps = iconDefaultProps;
ArrowLeftIcon.propTypes = iconPropTypes;
ArrowLeftIcon.defaultProps = iconDefaultProps;
XIcon.propTypes = iconPropTypes;
XIcon.defaultProps = iconDefaultProps;
SquarePenIcon.propTypes = iconPropTypes;
SquarePenIcon.defaultProps = iconDefaultProps;
LockKeyholeIcon.propTypes = iconPropTypes;
LockKeyholeIcon.defaultProps = iconDefaultProps;
LogOutIcon.propTypes = iconPropTypes;
LogOutIcon.defaultProps = iconDefaultProps;
PlusIcon.propTypes = iconPropTypes;
PlusIcon.defaultProps = iconDefaultProps;
CheckIcon.propTypes = iconPropTypes;
CheckIcon.defaultProps = iconDefaultProps;
TrashIcon.propTypes = iconPropTypes;
TrashIcon.defaultProps = iconDefaultProps;
ClockIcon.propTypes = iconPropTypes;
ClockIcon.defaultProps = iconDefaultProps;
InfoIcon.propTypes = iconPropTypes;
InfoIcon.defaultProps = iconDefaultProps;
MapIcon.propTypes = iconPropTypes;
MapIcon.defaultProps = iconDefaultProps;
SearchIcon.propTypes = iconPropTypes;
SearchIcon.defaultProps = iconDefaultProps;
HistoryIcon.propTypes = iconPropTypes;
HistoryIcon.defaultProps = iconDefaultProps;
EllipsisVerticalIcon.propTypes = iconPropTypes;
EllipsisVerticalIcon.defaultProps = iconDefaultProps;
Maximize2Icon.propTypes = iconPropTypes;
Maximize2Icon.defaultProps = iconDefaultProps;
Minimize2Icon.propTypes = iconPropTypes;
Minimize2Icon.defaultProps = iconDefaultProps;
SendIcon.propTypes = iconPropTypes;
SendIcon.defaultProps = iconDefaultProps;
PaperclipIcon.propTypes = iconPropTypes;
PaperclipIcon.defaultProps = iconDefaultProps;
UsersIcon.propTypes = iconPropTypes;
UsersIcon.defaultProps = iconDefaultProps;
SchedulingIcon.propTypes = iconPropTypes;
SchedulingIcon.defaultProps = iconDefaultProps;
DashboardIcon.propTypes = iconPropTypes;
DashboardIcon.defaultProps = iconDefaultProps;
TeamIcon.propTypes = iconPropTypes;
TeamIcon.defaultProps = iconDefaultProps;
JobsIcon.propTypes = iconPropTypes;
JobsIcon.defaultProps = iconDefaultProps;
ReportsIcon.propTypes = iconPropTypes;
ReportsIcon.defaultProps = iconDefaultProps;
DeficienciesIcon.propTypes = iconPropTypes;
DeficienciesIcon.defaultProps = iconDefaultProps;
ServiceRequestsIcon.propTypes = iconPropTypes;
ServiceRequestsIcon.defaultProps = iconDefaultProps;
InventoryIcon.propTypes = iconPropTypes;
InventoryIcon.defaultProps = iconDefaultProps;
BugTicketsIcon.propTypes = iconPropTypes;
BugTicketsIcon.defaultProps = iconDefaultProps;
SettingsIcon.propTypes = iconPropTypes;
SettingsIcon.defaultProps = iconDefaultProps;
GoToProfileIcon.propTypes = iconPropTypes;
GoToProfileIcon.defaultProps = iconDefaultProps;
ItemsNeededIcon.propTypes = iconPropTypes;
ItemsNeededIcon.defaultProps = iconDefaultProps;
ChatIcon.propTypes = iconPropTypes;
ChatIcon.defaultProps = iconDefaultProps;
OfflineIcon.propTypes = iconPropTypes;
OfflineIcon.defaultProps = iconDefaultProps;
NotificationIcon.propTypes = iconPropTypes;
NotificationIcon.defaultProps = iconDefaultProps;
